import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Play,
  TrendingUp,
  Database,
  Users,
  UserCheck,
  X,
} from "lucide-react";
import TutorialVideo from "../components/Dashboard/TutorialVideo";
import { Carousel, Modal } from "antd";
import "./LandingPage.css";
import Nvidia from "../assets/images/Nvidia.png";
import FinBlue from "../assets/images/FinBlue.png";
import FinGlobe from "../assets/images/FinGlobe.png";
import WadhwaniFoundation from "../assets/images/WadhwaniFoundation.jpg";
import IIMB from "../assets/images/IIMB.png";
import MinistryOFEIT from "../assets/images/MinistryOFEIT.png";
import MarwariCatalysts from "../assets/images/MarwariCatalysts.jpg";
import TypingSentence from "../components/ui/TickerHoverText"; // Assuming this path is correct
import MainLayout from "./MainLayout";

const SECONDARY_BTN =
  "border border-white text-white font-semibold rounded-full transition hover:bg-white hover:text-black hover:scale-105";

const LandingPage = () => {
  const navigate = useNavigate();

  const goToLogin = useCallback(() => navigate("/login"), [navigate]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const showModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };

  const userProfiles = [
    { role: "Audit Heads", count: 700, color: "from-blue-500 to-purple-500" },
    { role: "IT Teams", count: 200, color: "from-purple-500 to-pink-500" },
    { role: "Finance Teams", count: 500, color: "from-pink-500 to-red-500" },
  ];

  return (
    <>
      <MainLayout>
        <section className="w-full max-w-7xl mx-auto pt-32 pb-20 flex flex-col justify-center items-center text-center px-6 md:px-12 space-y-8">
          {/* Headline */}
          <h1 className="text-5xl md:text-7xl font-extrabold text-gray-200 leading-tight max-w-5xl">
            Your AI analyst that connects the dots hidden in data
          </h1>

          {/* Animated Text */}
          <TypingSentence
            text="Tell Xynex what you need. It finds the insight — no coding required."
            typingSpeed={60}
            deletingSpeed={35}
            pauseTime={2200}
          />

          <div className="flex gap-4 pt-4">
            <button
              onClick={showModal}
              className="flex items-center gap-3 px-8 py-4 rounded-full bg-gray-800 hover:bg-gray-700 transition hover:scale-105"
            >
              <Play className="w-6 h-6" />
              Getting Started: Watch Now
            </button>

            <button
              onClick={goToLogin}
              className={`${SECONDARY_BTN} px-8 py-3`}
            >
              Login
            </button>
          </div>
        </section>
        <div className="py-5 px-6 bg-black"></div>
        <div className="py-5 px-6 bg-black"></div>

        {/* Hero / Metrics */}
        <main className="w-full max-w-7xl mx-auto flex flex-col px-6 md:px-12 space-y-12 pb-24">
          <Carousel arrows autoplay className="w-full">
            {/* SLIDE 1 — TWO PARALLEL CARDS */}
            <div>
              <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
                {/* Card 1 */}
                <div className="relative flex items-center justify-between w-full md:w-[560px] h-[120px] px-8 rounded-2xl bg-black border border-red-500/20">
                  <div className="absolute top-0 right-30 w-64 h-64 bg-red-500/20 rounded-full blur-3xl animate-pulse"></div>

                  <div className="relative z-10 flex items-center gap-4">
                    <Activity className="w-8 h-8 text-red-400" />
                    <span className="text-lg font-semibold text-red-200">
                      Anomalies Detected
                    </span>
                  </div>

                  <div className="relative z-10 text-right">
                    <div className="text-4xl font-bold text-red-400">247</div>
                    <div className="flex items-center justify-end gap-1 text-green-400 text-sm">
                      <TrendingUp className="w-4 h-4" />
                      +12% today
                    </div>
                  </div>
                </div>

                {/* Card 2 */}
                <div className="relative flex items-center justify-between w-full md:w-[560px] h-[120px] px-8 rounded-2xl bg-black border border-blue-500/20">
                  <div className="absolute top-0 right-30 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>

                  <div className="relative z-10 flex items-center gap-4">
                    <Database className="w-8 h-8 text-blue-400" />
                    <span className="text-lg font-semibold text-blue-200">
                      Data Points Analyzed
                    </span>
                  </div>

                  <div className="relative z-10 text-right">
                    <div className="text-4xl font-bold text-blue-400">
                      1,250,000
                    </div>
                    <div className="flex items-center justify-end gap-1 text-green-400 text-sm">
                      <TrendingUp className="w-4 h-4" />
                      +10% today
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* SLIDE 2 — TWO PARALLEL CARDS */}
            <div>
              <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
                {/* Active Users Card */}
                <div className="relative flex items-center justify-between w-full md:w-[560px] h-[120px] px-8 rounded-2xl bg-black border border-purple-500/20">
                  <div className="absolute top-0 right-30 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>

                  <div className="relative z-10 flex items-center gap-4">
                    <Users className="w-8 h-8 text-purple-400" />
                    <span className="text-lg font-semibold text-purple-200">
                      Active Users
                    </span>
                  </div>

                  <div className="relative z-10 text-right">
                    <div className="text-4xl font-bold text-purple-400">
                      384
                    </div>
                    <div className="flex items-center justify-end gap-1 text-green-400 text-sm">
                      <TrendingUp className="w-4 h-4" />
                      +15% today
                    </div>
                  </div>
                </div>

                {/* Engaged Audience Card */}
                <div className="relative flex items-center justify-between w-full md:w-[560px] h-[120px] px-8 rounded-2xl bg-black border border-green-500/20">
                  <div className="absolute top-0 right-30 w-64 h-64 bg-green-500/20 rounded-full blur-3xl animate-pulse"></div>

                  <div className="relative z-10 flex items-center gap-4">
                    <TrendingUp className="w-8 h-8 text-green-400" />
                    <span className="text-lg font-semibold text-green-200">
                      Engaged Audience
                    </span>
                  </div>

                  <div className="relative z-10 text-right">
                    <div className="text-4xl font-bold text-green-400">87%</div>
                    <div className="flex items-center justify-end gap-1 text-green-400 text-sm">
                      <TrendingUp className="w-4 h-4" />
                      +10% today
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* SLIDE 3 — User Profiles Horizontal Card */}
            <div>
              <div className="flex justify-center">
                <div className="relative flex flex-col md:flex-row items-center justify-between w-full md:w-[1080px] min-h-[140px] px-8 py-6 rounded-2xl bg-black border border-pink-500/20 gap-6 md:gap-0">
                  <div className="absolute top-0 right-30 w-64 h-64 bg-pink-500/20 rounded-full blur-3xl animate-pulse"></div>

                  {/* Left Section */}
                  <div className="relative z-10 flex items-center gap-4 min-w-[260px] justify-center md:justify-start">
                    <UserCheck className="w-9 h-9 text-pink-400" />
                    <h3 className="text-xl font-semibold text-pink-200">
                      User Profiles
                    </h3>
                  </div>

                  {/* Right Section — Profiles */}
                  <div className="relative z-10 flex flex-col md:flex-row gap-4 w-full md:w-auto">
                    {userProfiles.map((profile, idx) => (
                      <div
                        key={idx}
                        className="group hover:scale-105 transition-transform cursor-pointer w-full md:w-auto"
                      >
                        <div
                          className={`bg-gradient-to-r ${profile.color} p-4 rounded-xl min-w-[220px]`}
                        >
                          <div className="flex justify-between items-center">
                            <span className="text-white font-medium">
                              {profile.role}
                            </span>
                            <span className="text-xl font-bold text-white">
                              {profile.count.toLocaleString()}
                            </span>
                          </div>

                          <div className="w-full bg-white/30 rounded-full h-1 mt-2">
                            <div
                              className="bg-white h-1 rounded-full transition-all duration-1000"
                              style={{
                                width: `${(profile.count / 1856) * 100}%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Carousel>
          <div className="py-5 px-6 bg-black"></div>

          {/* CTA SECTION */}
          <div className="flex flex-col items-center text-center space-y-10">
            {/* Recognitions */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold">Our Recognitions</h3>
              <div className="mt-8 flex flex-wrap justify-center items-center gap-10 opacity-90">
                {" "}
                <img src={Nvidia} alt="Nvidia" className="h-12" />{" "}
                <img src={FinBlue} alt="FinBlue" className="h-12" />{" "}
                <img src={FinGlobe} alt="FinGlobe" className="h-12" />{" "}
                <img
                  src={WadhwaniFoundation}
                  alt="WadhwaniFoundation"
                  className="h-12"
                />{" "}
                <img src={IIMB} alt="IIMB" className="h-12" />{" "}
                <img
                  src={MinistryOFEIT}
                  alt="Ministry of EIT"
                  className="h-12"
                />{" "}
                <img
                  src={MarwariCatalysts}
                  alt="Marwari Catalysts"
                  className="h-12"
                />{" "}
              </div>
            </div>
          </div>
        </main>
      </MainLayout>

      <Modal
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        centered
        destroyOnHidden
        width={800}
        closeIcon={<X style={{ color: "#fff", fontSize: 18 }} />}
        styles={{
          body: {
            padding: 24,
            maxHeight: "80vh",
            overflowY: "auto",
          },
          content: {
            background: "transparent",
          },
          mask: {
            backdropFilter: "blur(5px)",
            WebkitBackdropFilter: "blur(5px)",
            backgroundColor: "rgba(15, 23, 42, 0.6)",
          },
        }}
      >
        <TutorialVideo />
      </Modal>
    </>
  );
};

export default LandingPage;
